import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {Router} from '@angular/router';
import {forkJoin, interval, of, Subscription} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {
  faArrowRotateRight,
  faCircleInfo,
  faCube,
  faMagnifyingGlass,
  faPlay,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faFile,
  faFileExcel,
  faFilePdf,
  faFilePowerpoint,
  faFileWord,
} from '@fortawesome/pro-regular-svg-icons';
import {
  BicButtonComponent,
  BicDropdownElementComponent,
  BicPopupComponent,
  BicSelectComponent,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {ProjectFile, ProjectFileControllerService} from '../../../../api';
import {DocumentClassService} from '../../../files/components/document-classes/document-class.service';
import {filesForRuleset, RulesetFileConstraints, rulesetAllowedFileTypes} from '../../../../models/ruleset-file-match';
import {RuleTestService} from '../../../../services/rule-test/rule-test.service';
import {
  RuleTestFileRef,
  RuleTestPopupContext,
  RuleTestQuota,
  RuleTestRuleOption,
  RuleTestRunView,
  RuleTestStartResult,
} from '../../../../services/rule-test/rule-test.models';
import {elapsedMs, formatClockTime, formatDuration, ruleTestLimitTooltip} from '../../../../services/rule-test/rule-test.format';
import {RuleTestResultPreviewComponent} from '../rule-test-result-preview/rule-test-result-preview.component';
import {QuotaSegmentsComponent} from '../../../util/quota-segments/quota-segments.component';
import {faIfc} from '../../../util/icons/custom-icons';

/** Kein Tooltip: die Direktive zeigt bei `''` einen leeren Pfeil, nur `undefined` blendet aus. */
const NO_TOOLTIP = undefined as unknown as string;

/** Was die Preview-Fläche gerade zeigt (Figma 02 / 03 / 04 / 07). */
export type RuleTestPopupView = 'idle' | 'running' | 'result' | 'error';

/** Eine wählbare Datei im Datei-Select. */
export interface RuleTestFileOption {
  id: string;
  name: string;
  icon: IconDefinition;
}

/** Eine Zeile „Dokumententyp → Datei" beim Cross Check. */
export interface RuleTestPoolRow {
  key: string;
  name: string;
  options: RuleTestFileOption[];
  /** Nach der Suche sichtbare Optionen (die gewählte Datei bleibt immer drin). */
  visibleOptions: RuleTestFileOption[];
  search: string;
  /** Die Suche trifft keine Datei. */
  searchEmpty: boolean;
  hint: string;
  selectedFileId: string | null;
}

/** Ab so vielen Dateien bekommt das Datei-Select ein Suchfeld – darunter ist es nur Ballast. */
export const FILE_SEARCH_MIN_OPTIONS = 6;

/**
 * Filtert Dateien nach einem Suchbegriff: jedes Wort muss im Namen vorkommen, ohne Groß/Klein.
 * Die gewählte Datei bleibt immer in der Liste – `bic-select` liest seine Anzeige aus den
 * vorhandenen Optionen und zeigte sonst wieder den Platzhalter.
 */
export function filterFileOptions(
  options: RuleTestFileOption[],
  term: string,
  keepId: string | null,
): {visible: RuleTestFileOption[]; empty: boolean} {
  const words = term.normalize('NFC').toLocaleLowerCase('de').split(/\s+/).filter(Boolean);
  if (!words.length) return {visible: options, empty: false};
  const matches = options.filter(option => {
    const name = option.name.normalize('NFC').toLocaleLowerCase('de');
    return words.every(word => name.includes(word));
  });
  const kept = keepId && !matches.some(option => option.id === keepId)
    ? options.filter(option => option.id === keepId)
    : [];
  return {visible: [...kept, ...matches], empty: matches.length === 0};
}

interface RuleSelectOption {
  key: string;
  label: string;
}

const FILE_EXTENSION_LABEL: Record<string, string> = {
  PDF: '.pdf',
  EXCEL: '.xlsx',
  WORD: '.docx',
  POWERPOINT: '.pptx',
  IFC: '.ifc',
  COORDINATION: 'Koordinationsmodell',
  UNKNOWN: 'unbekannt',
};

/**
 * Popup „Prüfregel testen" (Figma Frames 02–07, Plan §3): eine Prüfregel im ungespeicherten
 * Editor-Stand an einer Projektdatei ausprobieren.
 *
 * Der Lauf gehört dem `RuleTestService`, nicht dem Popup: Schließen beendet ihn nicht, beim erneuten
 * Öffnen zeigt das Popup den laufenden bzw. letzten Lauf des Kontexts wieder an.
 *
 * Der Editor übergibt `context` als FELD, das er beim Öffnen einmal berechnet – nie als Getter
 * (siehe Frontend/AGENTS.md, „Template Bindings & Change Detection"). Alles, was das Template an
 * Inputs oder `@for` bindet, steht hier ebenfalls in Feldern.
 *
 * Achtung beim Einbinden: `bic-button` rendert ein `<button>` ohne `type`. Innerhalb eines
 * `<form>` würde jeder Klick das Formular absenden – das Popup gehört deshalb außerhalb davon.
 */
@Component({
  selector: 'app-rule-test-popup',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BicPopupComponent,
    BicButtonComponent,
    BicSelectComponent,
    BicDropdownElementComponent,
    TooltipDirective,
    FaIconComponent,
    QuotaSegmentsComponent,
    RuleTestResultPreviewComponent,
  ],
  templateUrl: './rule-test-popup.component.html',
})
export class RuleTestPopupComponent implements OnChanges, OnDestroy {
  private ruleTestService = inject(RuleTestService);
  private fileApi = inject(ProjectFileControllerService);
  private documentClassService = inject(DocumentClassService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  @Input() context: RuleTestPopupContext | null = null;
  @Output() closed = new EventEmitter<void>();

  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faCube = faCube;
  protected readonly faPlay = faPlay;
  protected readonly faArrowRotateRight = faArrowRotateRight;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  protected readonly faXmark = faXmark;
  protected readonly faFile = faFile;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly fileSearchMinOptions = FILE_SEARCH_MIN_OPTIONS;

  // ─── Zustand ───────────────────────────────────────────────────────────────────────────────
  view: RuleTestPopupView = 'idle';
  run: RuleTestRunView | null = null;
  quota: RuleTestQuota | null = null;

  ruleOptions: RuleSelectOption[] = [];
  selectedRuleKey: string | null = null;
  ruleHint = '';
  /** Bei IDS heißt eine Prüfregel „Spezifikation" (Regel = Spezifikation des IDS). */
  ruleFieldLabel = 'Prüfregel *';
  rulePlaceholder = 'Prüfregel auswählen';
  /** Für Fließtexte: „eine Prüfregel" bzw. „eine Spezifikation". */
  ruleNoun = 'Prüfregel';

  fileOptions: RuleTestFileOption[] = [];
  /** Nach der Suche sichtbare Dateien – als Feld, nie als Getter (Template-Binding-Regel). */
  visibleFileOptions: RuleTestFileOption[] = [];
  fileSearch = '';
  fileSearchEmpty = false;
  showFileSearch = false;
  selectedFileId: string | null = null;
  selectedFileIcon: IconDefinition = faFile;
  fileHint = '';
  poolRows: RuleTestPoolRow[] = [];
  /** Cross Check: eine Datei je Pool statt eines einzigen Datei-Selects. */
  poolMode = false;

  filesLoading = true;
  /** Mindestens ein Datei-Select hat keine einzige passende Datei. */
  noFiles = false;
  uploadHref = '';

  /** Hinweise aus dem Editor-Stand plus die 422-Meldungen des Servers. */
  blockingErrors: string[] = [];
  /** Start scheiterte technisch, bevor ein Lauf entstand (Netz, 5xx). */
  startError: string | null = null;
  /** 409: der Nutzer hat schon einen laufenden Testlauf. */
  activeRunId: string | null = null;
  starting = false;

  // ─── abgeleitet (updateDerived) ────────────────────────────────────────────────────────────
  limitReached = false;
  canStart = false;
  startTooltip = NO_TOOLTIP;
  startTooltipSubtext = NO_TOOLTIP;
  primaryLabel = 'Testlauf starten';
  secondaryLabel = 'Abbrechen';
  primaryIcon: IconDefinition = faPlay;
  ruleSelectDisabled = false;
  fileSelectDisabled = false;

  progressPercent = 0;
  progressStepText = '';
  elapsedLabel = '0:00';
  runSummary = '';
  errorMessage = '';

  private serverErrors: string[] = [];
  private allFiles: ProjectFile[] = [];
  private classTitleById = new Map<string, string>();
  private selectionFromRunApplied = false;
  /** Cross Check: Auswahl aus einem Lauf, die erst nach dem Laden der Dateien greifen kann. */
  private pendingPoolSelection = new Map<string, string>();
  private uploadLinkOpened = false;

  private quotaSub: Subscription;
  private runSub: Subscription | null = null;
  private loadSub: Subscription | null = null;
  private startSub: Subscription | null = null;
  private tickSub: Subscription | null = null;

  constructor() {
    this.quotaSub = this.ruleTestService.quota$.subscribe(quota => {
      this.quota = quota;
      this.updateDerived();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['context']) {
      this.initContext();
    }
  }

  ngOnDestroy(): void {
    this.ruleTestService.setPopupOpen(null);
    this.quotaSub.unsubscribe();
    this.runSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.startSub?.unsubscribe();
    this.stopTicking();
  }

  /** Nach dem Hochladen in einem anderen Tab: Dateien neu laden, sobald der Nutzer zurück ist. */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!this.context || this.view === 'running') return;
    if (this.uploadLinkOpened || this.noFiles) {
      this.uploadLinkOpened = false;
      this.loadFiles();
    }
  }

  // ─── Aktionen ──────────────────────────────────────────────────────────────────────────────

  onRuleChange(key: string): void {
    if (key === this.selectedRuleKey) return;
    this.selectedRuleKey = key;
    this.serverErrors = [];
    this.refilter();
  }

  onFileChange(fileId: string): void {
    this.selectedFileId = fileId || null;
    this.serverErrors = [];
    this.selectedFileIcon = this.fileOptions.find(option => option.id === this.selectedFileId)?.icon ?? faFile;
    this.updateVisibleFileOptions();
    this.updateDerived();
  }

  onFileSearch(term: string): void {
    this.fileSearch = term;
    this.updateVisibleFileOptions();
  }

  onPoolFileChange(poolKey: string, fileId: string): void {
    this.poolRows = this.poolRows.map(row => row.key === poolKey
      ? withVisibleOptions({...row, selectedFileId: fileId || null})
      : row);
    this.serverErrors = [];
    this.updateDerived();
  }

  onPoolSearch(poolKey: string, term: string): void {
    this.poolRows = this.poolRows.map(row => row.key === poolKey ? withVisibleOptions({...row, search: term}) : row);
  }

  private updateVisibleFileOptions(): void {
    const {visible, empty} = filterFileOptions(this.fileOptions, this.fileSearch, this.selectedFileId);
    this.visibleFileOptions = visible;
    this.fileSearchEmpty = empty;
    this.showFileSearch = this.fileOptions.length >= FILE_SEARCH_MIN_OPTIONS;
  }

  onUploadLinkClick(): void {
    this.uploadLinkOpened = true;
  }

  /** „Testlauf starten", „Erneut testen" und „Erneut versuchen" – jeder Start verbraucht einen Lauf. */
  start(): void {
    const context = this.context;
    if (!context || !this.canStart || !this.selectedRuleKey) return;
    const files = this.selectedFiles();
    if (!files) return;

    const request = context.buildRequest(this.selectedRuleKey, files);
    this.starting = true;
    this.serverErrors = [];
    this.startError = null;
    this.activeRunId = null;
    this.updateDerived();

    this.startSub?.unsubscribe();
    this.startSub = this.ruleTestService.start(context.projectId, context.contextKey, request)
      .subscribe(result => this.onStartResult(result));
  }

  /** 409: den bereits laufenden Testlauf in diesem Popup verfolgen. */
  openActiveRun(): void {
    const context = this.context;
    if (!context || !this.activeRunId) return;
    this.ruleTestService.attach(context.projectId, context.contextKey, this.activeRunId);
    this.activeRunId = null;
    this.updateDerived();
  }

  close(): void {
    this.closed.emit();
  }

  // ─── intern ────────────────────────────────────────────────────────────────────────────────

  private initContext(): void {
    this.runSub?.unsubscribe();
    this.runSub = null;
    this.serverErrors = [];
    this.startError = null;
    this.activeRunId = null;
    this.starting = false;
    this.selectionFromRunApplied = false;
    this.run = null;

    const context = this.context;
    if (!context) {
      this.ruleTestService.setPopupOpen(null);
      this.updateDerived();
      return;
    }

    this.poolMode = !!context.pools?.length;
    this.ruleOptions = context.rules.map(rule => ({key: rule.key, label: ruleLabel(rule)}));
    const preselected = context.rules.find(rule => rule.key === context.preselectedRuleKey);
    this.selectedRuleKey = preselected?.key ?? context.rules[0]?.key ?? null;
    this.selectedFileId = null;
    this.poolRows = [];
    const count = context.rules.length;
    const ids = context.rulesetType === 'IDS';
    this.ruleNoun = ids ? 'Spezifikation' : 'Prüfregel';
    this.ruleFieldLabel = `${this.ruleNoun} *`;
    this.rulePlaceholder = `${this.ruleNoun} auswählen`;
    this.ruleHint = ids
      ? `Aus IDS „${context.setLabel}“ · ${count} ${count === 1 ? 'Spezifikation' : 'Spezifikationen'}`
      : `Aus Prüfregelset „${context.setLabel}“ · ${count} ${count === 1 ? 'Prüfregel' : 'Prüfregeln'}`;
    this.uploadHref = this.router.serializeUrl(this.router.createUrlTree(['/projects', context.projectId, 'files']));

    this.ruleTestService.setPopupOpen(context.contextKey);
    this.ruleTestService.markSeen(context.contextKey);
    this.ruleTestService.refreshQuota();

    this.runSub = this.ruleTestService.run$(context.contextKey).subscribe(run => this.onRun(run));
    this.loadFiles();
  }

  private onRun(run: RuleTestRunView | null): void {
    this.run = run;
    // Beim Wiederöffnen die Auswahl des gezeigten Laufs übernehmen, damit Preview und Felder passen.
    if (run && !this.selectionFromRunApplied) {
      this.selectionFromRunApplied = true;
      this.applySelectionFrom(run);
    }
    if (run) {
      this.startError = null;
    }
    this.updateDerived();
  }

  private applySelectionFrom(run: RuleTestRunView): void {
    if (this.context?.rules.some(rule => rule.key === run.ruleKey)) {
      this.selectedRuleKey = run.ruleKey;
    }
    if (this.poolMode) {
      this.pendingPoolSelection = new Map((run.files ?? [])
        .filter(file => !!file.poolKey)
        .map(file => [file.poolKey as string, file.fileId]));
    } else {
      this.selectedFileId = run.files?.[0]?.fileId ?? this.selectedFileId;
    }
    if (!this.filesLoading) {
      this.refilter();
    }
  }

  private loadFiles(): void {
    const context = this.context;
    if (!context) return;
    this.filesLoading = true;
    this.updateDerived();
    this.loadSub?.unsubscribe();
    this.loadSub = forkJoin({
      files: this.fileApi.getFiles(context.projectId).pipe(catchError(() => of([] as ProjectFile[]))),
      classes: this.documentClassService.list(context.projectId).pipe(catchError(() => of(null))),
    }).subscribe(({files, classes}) => {
      // Wie in step-combined-select: nur fertig verarbeitete Dateien, keine Ordner.
      this.allFiles = (files ?? []).filter(file =>
        !file.isFolder
        && file.fileType !== ProjectFile.FileTypeEnum.FOLDER
        && file.fileStatus === ProjectFile.FileStatusEnum.READY
        && !!file.id);
      this.classTitleById = buildClassTitleMap(classes?.classes ?? []);
      this.filesLoading = false;
      this.refilter();
    });
  }

  /** Dateioptionen und Hinweise für die gewählte Regel (bzw. je Pool) neu bestimmen. */
  private refilter(): void {
    const context = this.context;
    if (!context) return;

    if (this.poolMode) {
      const previous = new Map(this.poolRows.map(row => [row.key, row.selectedFileId]));
      const previousSearch = new Map(this.poolRows.map(row => [row.key, row.search]));
      this.poolRows = (context.pools ?? []).map(pool => {
        const constraints: RulesetFileConstraints = {
          allowedFileTypes: pool.allowedFileTypes,
          allowedDocumentClasses: pool.allowedDocumentClasses,
        };
        const options = this.optionsFor(constraints);
        const wanted = this.pendingPoolSelection.get(pool.key) ?? previous.get(pool.key) ?? null;
        return withVisibleOptions({
          key: pool.key,
          name: pool.name,
          options,
          visibleOptions: options,
          search: previousSearch.get(pool.key) ?? '',
          searchEmpty: false,
          hint: this.buildFileHint(constraints, false),
          selectedFileId: wanted && options.some(option => option.id === wanted) ? wanted : null,
        });
      });
      if (!this.filesLoading) {
        this.pendingPoolSelection.clear();
      }
      this.noFiles = !this.filesLoading && this.poolRows.some(row => row.options.length === 0);
    } else {
      const constraints = this.effectiveConstraints();
      this.fileOptions = this.optionsFor(constraints);
      if (!this.filesLoading && this.selectedFileId && !this.fileOptions.some(option => option.id === this.selectedFileId)) {
        // Die Auswahl passt nicht zur neu gewählten Regel → zurücksetzen statt still Falsches zu testen.
        this.selectedFileId = null;
      }
      this.selectedFileIcon = this.fileOptions.find(option => option.id === this.selectedFileId)?.icon ?? faFile;
      this.updateVisibleFileOptions();
      this.fileHint = this.buildFileHint(constraints, true);
      this.noFiles = !this.filesLoading && this.fileOptions.length === 0;
    }
    this.updateDerived();
  }

  /** Einschränkungen der gewählten Regel; was die Regel nicht einschränkt, gibt das Set vor. */
  private effectiveConstraints(): RulesetFileConstraints {
    const context = this.context;
    const rule = context?.rules.find(candidate => candidate.key === this.selectedRuleKey);
    return {
      allowedFileTypes: rule?.allowedFileTypes?.length ? rule.allowedFileTypes : (context?.setAllowedFileTypes ?? []),
      allowedDocumentClasses: rule?.allowedDocumentClasses?.length
        ? rule.allowedDocumentClasses
        : (context?.setAllowedDocumentClasses ?? []),
    };
  }

  private optionsFor(constraints: RulesetFileConstraints): RuleTestFileOption[] {
    return filesForRuleset(constraints, this.allFiles, this.classTitleById)
      // NFC: Dateinamen vom Mac kommen zerlegt an („o" + Trema) – sonst stünde „Lo¨sch…" in der Liste
      // und die Suche nach „lösch" fände die Datei nicht.
      .map(file => ({id: file.id as string, name: (file.name ?? '').normalize('NFC'), icon: fileIcon(file.fileType)}))
      .sort((a, b) => a.name.localeCompare(b.name, 'de', {numeric: true}));
  }

  /** „Aus „Projekt“ · erlaubt: .pdf, .xlsx · Raumbuch, LV" (Figma 02). */
  private buildFileHint(constraints: RulesetFileConstraints, withProject: boolean): string {
    const parts: string[] = [];
    if (withProject && this.context?.projectName) {
      parts.push(`Aus „${this.context.projectName}“`);
    }
    const types = rulesetAllowedFileTypes(constraints).map(type => FILE_EXTENSION_LABEL[type] ?? type.toLowerCase());
    parts.push(types.length ? `erlaubt: ${types.join(', ')}` : 'alle Dateitypen');
    const titles: string[] = [];
    for (const id of constraints.allowedDocumentClasses ?? []) {
      const title = this.classTitleById.get(normalizeClassKey(id));
      if (title && !titles.includes(title)) titles.push(title);
    }
    if (titles.length) {
      parts.push(titles.join(', '));
    }
    return parts.join(' · ');
  }

  private selectedFiles(): RuleTestFileRef[] | null {
    if (this.poolMode) {
      if (!this.poolRows.length || this.poolRows.some(row => !row.selectedFileId)) return null;
      return this.poolRows.map(row => ({fileId: row.selectedFileId as string, poolKey: row.key}));
    }
    return this.selectedFileId ? [{fileId: this.selectedFileId}] : null;
  }

  private onStartResult(result: RuleTestStartResult): void {
    this.starting = false;
    switch (result.kind) {
      case 'started':
        // Den Stand liefert run$ – hier nur die Auswahl dieses Laufs festhalten.
        this.selectionFromRunApplied = true;
        break;
      case 'invalid':
        this.serverErrors = result.errors;
        break;
      case 'limit':
        // quota$ trägt den erschöpften Stand bereits; Button und Tooltip folgen daraus.
        break;
      case 'active':
        this.activeRunId = result.runId;
        break;
      case 'error':
        this.startError = result.message;
        break;
    }
    this.updateDerived();
  }

  private computeView(): RuleTestPopupView {
    if (this.run?.status === 'RUNNING') return 'running';
    if (this.startError) return 'error';
    if (this.run?.status === 'COMPLETED') return 'result';
    if (this.run?.status === 'FAILED') return 'error';
    return 'idle';
  }

  private updateDerived(): void {
    const context = this.context;
    const ruleErrors = (this.selectedRuleKey && context?.ruleValidationErrors?.[this.selectedRuleKey]) || [];
    this.blockingErrors = dedupe([...(context?.validationErrors ?? []), ...ruleErrors, ...this.serverErrors]);
    this.view = this.computeView();
    const running = this.view === 'running';
    this.limitReached = !!this.quota && this.quota.remaining <= 0;
    this.fileSelectDisabled = running || this.starting;
    this.ruleSelectDisabled = this.fileSelectDisabled || !!context?.ruleLocked;

    const hasRule = !!this.selectedRuleKey;
    const hasFiles = !!this.selectedFiles();
    this.canStart = !!context && !running && !this.starting && !this.limitReached
      && this.blockingErrors.length === 0 && !this.noFiles && !this.filesLoading && hasRule && hasFiles;

    // Tooltip nur, wenn der Button aus einem erklärbaren Grund gesperrt ist.
    this.startTooltip = NO_TOOLTIP;
    this.startTooltipSubtext = NO_TOOLTIP;
    if (!running && !this.starting) {
      const limitTooltip = this.limitReached ? ruleTestLimitTooltip(this.quota) : null;
      if (limitTooltip) {
        this.startTooltip = limitTooltip.title;
        this.startTooltipSubtext = limitTooltip.text;
      } else if (this.blockingErrors.length) {
        this.startTooltip = 'Bitte zuerst die Hinweise beheben';
      } else if (this.noFiles) {
        this.startTooltip = 'Keine passende Datei im Projekt';
      } else if (!this.filesLoading && (!hasRule || !hasFiles)) {
        this.startTooltip = this.poolMode
          ? 'Bitte für jeden Dokumententyp eine Datei wählen'
          : `Bitte eine Datei und eine ${this.ruleNoun} wählen`;
      }
    }

    switch (this.view) {
      case 'running':
        this.primaryLabel = 'Testlauf läuft …';
        this.primaryIcon = faPlay;
        break;
      case 'result':
        this.primaryLabel = 'Erneut testen';
        this.primaryIcon = faPlay;
        break;
      case 'error':
        this.primaryLabel = 'Erneut versuchen';
        this.primaryIcon = faArrowRotateRight;
        break;
      default:
        this.primaryLabel = 'Testlauf starten';
        this.primaryIcon = faPlay;
    }
    this.secondaryLabel = this.view === 'idle' ? 'Abbrechen' : 'Schließen';

    const run = this.run;
    if (run && running) {
      const progress = run.progress;
      this.progressPercent = clampPercent(progress?.percent);
      this.progressStepText = progress?.stepCount ? `Schritt ${progress.stepIndex} von ${progress.stepCount}` : '';
      const fileNames = (run.files ?? []).map(file => file.fileName).filter(Boolean).join(', ');
      const started = formatClockTime(run.startedAt);
      this.runSummary = [run.ruleLabel, fileNames, started ? `Gestartet ${started}` : '']
        .filter(Boolean).join(' · ');
      this.elapsedLabel = formatDuration(elapsedMs(run.startedAt));
      this.startTicking();
    } else {
      this.stopTicking();
    }

    this.errorMessage = this.startError
      ?? run?.failureMessage
      ?? 'Der Testlauf ist technisch fehlgeschlagen.';

    this.cdr.markForCheck();
  }

  /** „Dauer: m:ss" im Sekundentakt, solange der Lauf läuft. */
  private startTicking(): void {
    if (this.tickSub) return;
    this.tickSub = interval(1000).subscribe(() => {
      if (this.run?.status !== 'RUNNING') {
        this.stopTicking();
        return;
      }
      this.elapsedLabel = formatDuration(elapsedMs(this.run.startedAt));
      this.cdr.markForCheck();
    });
  }

  private stopTicking(): void {
    this.tickSub?.unsubscribe();
    this.tickSub = null;
  }
}

function ruleLabel(rule: RuleTestRuleOption): string {
  return rule.shortCode ? `${rule.shortCode} · ${rule.name}` : rule.name;
}

function fileIcon(fileType: ProjectFile.FileTypeEnum | undefined): IconDefinition {
  switch (fileType) {
    case 'PDF':
      return faFilePdf;
    case 'EXCEL':
      return faFileExcel;
    case 'WORD':
      return faFileWord;
    case 'POWERPOINT':
      return faFilePowerpoint;
    case 'IFC':
      return faIfc as IconDefinition;
    default:
      return faFile;
  }
}

function normalizeClassKey(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

/** ID→Titel der Dokumentenklassen, auch unter `seed:<KEY>` (global geteilte Sets), kleingeschrieben. */
function buildClassTitleMap(classes: {id?: string; title?: string; seedKey?: string | null}[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const documentClass of classes) {
    const id = normalizeClassKey(documentClass.id);
    const title = String(documentClass.title ?? '').trim();
    if (!id || !title) continue;
    map.set(id, title);
    if (documentClass.seedKey) {
      map.set(normalizeClassKey('seed:' + documentClass.seedKey), title);
    }
  }
  return map;
}

function dedupe(values: string[]): string[] {
  return values.filter((value, index) => !!value && values.indexOf(value) === index);
}

function clampPercent(value: number | null | undefined): number {
  if (value == null || !isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

/** Setzt die sichtbaren Optionen einer Pool-Zeile nach Suche und Auswahl. */
function withVisibleOptions(row: RuleTestPoolRow): RuleTestPoolRow {
  const {visible, empty} = filterFileOptions(row.options, row.search, row.selectedFileId);
  return {...row, visibleOptions: visible, searchEmpty: empty};
}
